import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MdSearch, MdShoppingCart, MdReceipt, MdArrowForward, MdInfo, MdBusiness, MdCalendarToday, MdLocalShipping } from 'react-icons/md';
import { ClearFiltersButton, PageHeader, PageSkeleton } from '@euroasiann/shared/ui';
import { authenticatedFetch } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Pagination } from '../../components/shared/Pagination';

interface PurchaseOrder {
    _id: string;
    poNumber: string;
    title: string;
    status: string;
    totalAmount: number;
    currency: string;
    createdAt: string;
    paymentStatus?: string;
    organizationId: { name: string };
    vendorOrganizationId: { name: string };
}

export type POStatusScope = 'all' | 'open' | 'in-progress' | 'delivered';

const IN_PROGRESS_STATUSES = ['issued', 'acknowledged', 'in_progress', 'shipped'];
const OPEN_STATUSES = ['draft', 'issued', 'acknowledged', 'in_progress', 'shipped'];

export function POListPage({
    direction = 'received',
    embedded = false,
    statusScope = 'all',
}: {
    direction?: 'received' | 'sent';
    embedded?: boolean;
    /** When embedded in Sales: open = not delivered, in-progress = active fulfilment, delivered = delivered only */
    statusScope?: POStatusScope;
}) {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeStatus, setActiveStatus] = useState(
        statusScope === 'delivered' ? 'delivered' : 'all'
    );
    const PAGE_SIZE = 10;
    const [page, setPage] = useState(1);
    const isReceived = direction === 'received';

    useEffect(() => {
        if (statusScope === 'delivered') setActiveStatus('delivered');
        else if (statusScope === 'in-progress' || statusScope === 'open') setActiveStatus('all');
    }, [statusScope]);

    const statusInParam = useMemo(() => {
        if (activeStatus !== 'all') return null;
        if (statusScope === 'in-progress') return IN_PROGRESS_STATUSES.join(',');
        if (statusScope === 'open') return OPEN_STATUSES.join(',');
        return null;
    }, [activeStatus, statusScope]);

    const { data: orders, isLoading, error, isFetching } = useQuery({
        queryKey: ['admin-pos', direction, activeStatus, statusScope, statusInParam],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (activeStatus !== 'all') {
                params.append('status', activeStatus);
            } else if (statusScope === 'delivered') {
                params.append('status', 'delivered');
            } else if (statusInParam) {
                params.append('statusIn', statusInParam);
            }
            params.append('direction', direction);
            params.append('limit', '100');
            const res = await authenticatedFetch(`/api/v1/admin/pos?${params.toString()}`);
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.error || 'Failed to fetch POs');
            }
            const json = await res.json();
            const raw = json.data?.data ?? json.data;
            return Array.isArray(raw) ? raw : [];
        },
        staleTime: 0,
        refetchOnMount: 'always',
    });

    const filteredOrders = useMemo(() => {
        let list = orders || [];

        // Always re-assert status against the active chip so badges match the filter
        if (activeStatus !== 'all') {
            list = list.filter((po: PurchaseOrder) => po.status === activeStatus);
        } else if (statusScope === 'in-progress') {
            list = list.filter((po: PurchaseOrder) => IN_PROGRESS_STATUSES.includes(po.status));
        } else if (statusScope === 'open') {
            list = list.filter((po: PurchaseOrder) => OPEN_STATUSES.includes(po.status));
        } else if (statusScope === 'delivered') {
            list = list.filter((po: PurchaseOrder) => po.status === 'delivered');
        }

        if (!searchQuery.trim()) return list;
        const query = searchQuery.toLowerCase();
        return list.filter((po: PurchaseOrder) =>
            po.poNumber.toLowerCase().includes(query) ||
            po.title.toLowerCase().includes(query) ||
            po.organizationId?.name.toLowerCase().includes(query) ||
            po.vendorOrganizationId?.name.toLowerCase().includes(query)
        );
    }, [orders, searchQuery, statusScope, activeStatus]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, activeStatus, statusScope]);
    
    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const paginatedOrders = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredOrders.slice(start, start + PAGE_SIZE);
    }, [filteredOrders, currentPage]);

    const statuses = useMemo(() => {
        if (statusScope === 'delivered') {
            return [{ id: 'delivered', label: 'Delivered' }];
        }
        if (statusScope === 'in-progress') {
            return [
                { id: 'all', label: 'All In Progress' },
                { id: 'issued', label: 'Issued' },
                { id: 'acknowledged', label: 'Acknowledged' },
                { id: 'in_progress', label: 'In Fulfilment' },
                { id: 'shipped', label: 'Shipped' },
            ];
        }
        if (statusScope === 'open') {
            return [
                { id: 'all', label: 'All Open' },
                { id: 'draft', label: 'Draft' },
                { id: 'issued', label: 'Issued' },
                { id: 'acknowledged', label: 'Acknowledged' },
                { id: 'in_progress', label: 'In Fulfilment' },
                { id: 'shipped', label: 'Shipped' },
            ];
        }
        return [
            { id: 'all', label: 'All Orders' },
            { id: 'draft', label: 'Draft' },
            { id: 'issued', label: 'Issued' },
            { id: 'acknowledged', label: 'Acknowledged' },
            { id: 'in_progress', label: 'In Fulfilment' },
            { id: 'shipped', label: 'Shipped' },
            { id: 'delivered', label: 'Delivered' },
        ];
    }, [statusScope]);

    const title =
        statusScope === 'in-progress'
            ? 'In Progress'
            : statusScope === 'delivered'
              ? 'Orders Delivered'
              : isReceived
                ? 'Purchase Order Received'
                : 'Purchase Order Sent';

    const subtitle =
        statusScope === 'in-progress'
            ? 'Orders after PO confirmation that are still being fulfilled (proforma, shipping, delivery).'
            : statusScope === 'delivered'
              ? 'Completed purchase orders that have been delivered to the customer.'
              : isReceived
                ? 'Orders received from customers after they confirm a quotation or offer.'
                : 'Orders you sent to vendors after confirming a quotation.';

    if (isLoading && !orders) {
        return <PageSkeleton variant="table" />;
    }

    if (error) {
        return (
            <div className={cn(embedded ? 'p-0' : 'p-8', 'bg-[hsl(var(--background))]', !embedded && 'min-h-screen')}>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 flex items-center gap-4 shadow-sm">
                    <MdInfo className="w-8 h-8 flex-shrink-0" />
                    <div>
                        <h2 className="font-bold text-lg">Failed to load orders</h2>
                        <p>{(error as Error).message}</p>
                    </div>
                </div>
            </div>
        );
    }

    const defaultStatus = statusScope === 'delivered' ? 'delivered' : 'all';

    const filtersBar = (
        <div className="flex flex-col gap-3 w-full min-w-0">
            <div className="flex flex-wrap items-center gap-3 w-full">
                <div className="relative w-full max-w-full sm:max-w-md flex-1 min-w-[200px]">
                    <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0088cc]/65 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Find PO Number, User, or Entity..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-[#0088cc]/20 rounded-xl bg-white focus:outline-none focus:border-[#0088cc] focus:ring-1 focus:ring-[#0088cc] shadow-sm transition-all text-sm font-medium text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 dark:bg-[hsl(var(--card))]"
                    />
                </div>
                <ClearFiltersButton
                    visible={!!searchQuery.trim() || activeStatus !== defaultStatus}
                    onClick={() => {
                        setSearchQuery('');
                        setActiveStatus(defaultStatus);
                    }}
                    variant="toolbar"
                />
            </div>

            {statuses.length > 1 && (
                <div className="flex w-full max-w-full overflow-x-auto no-scrollbar rounded-xl shadow-sm border border-[#0088cc]/15 bg-white p-1 dark:bg-[hsl(var(--card))]">
                    {statuses.map((filter) => (
                        <button
                            key={filter.id}
                            type="button"
                            onClick={() => setActiveStatus(filter.id)}
                            className={`px-3 sm:px-4 py-2 rounded-lg transition-all whitespace-nowrap text-xs sm:text-sm flex-shrink-0 ${
                                activeStatus === filter.id
                                    ? 'bg-[#0088cc] text-white font-bold shadow-md'
                                    : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[#0088cc]/5'
                            }`}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
            )}
            {isFetching && (
                <p className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">Refreshing orders…</p>
            )}
        </div>
    );

    return (
        <div
            className={cn(
                'w-full min-w-0 overflow-x-hidden bg-[hsl(var(--background))] font-sans text-[hsl(var(--foreground))]',
                embedded ? '' : 'min-h-screen p-3 sm:p-4 lg:p-8'
            )}
        >
            {!embedded && (
                <PageHeader
                    title={title}
                    subtitle={subtitle}
                    subtitleVariant="description"
                    subtitleAccent={false}
                    breadcrumb={[
                        { label: 'Admin' },
                        { label: title, current: true },
                    ]}
                />
            )}

            <div className={cn('mb-4 space-y-3 min-w-0', !embedded && 'mt-2')}>
                {embedded && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{subtitle}</p>
                )}
                {filtersBar}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:gap-4 min-w-0 mb-8 pb-4">
                {filteredOrders.length > 0 ? (
                    paginatedOrders.map((po: any) => (
                        <div
                            key={po._id}
                            onClick={() => navigate(`/dashboard/admin/pos/${po._id}`)}
                            className="bg-white rounded-xl border border-[#0088cc]/20 p-4 sm:p-5 lg:p-6 hover:border-[#0088cc] shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col lg:flex-row lg:items-center justify-between gap-4 sm:gap-5 relative group dark:bg-[hsl(var(--card))] min-w-0"
                        >
                            <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0 flex-1">
                                <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#0088cc]/10 flex items-center justify-center text-[#0088cc] flex-shrink-0 group-hover:scale-110 transition-transform">
                                    <MdShoppingCart className="w-5 h-5 sm:w-7 sm:h-7" />
                                </div>

                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                                        <h3 className="text-base sm:text-lg font-bold text-[hsl(var(--foreground))] tracking-tight group-hover:text-[#0088cc] transition-colors break-all">
                                            {po.poNumber}
                                        </h3>
                                        <div className={cn(
                                            "px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border shadow-xs",
                                            po.status === 'delivered' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                            po.status === 'shipped' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                                            po.status === 'acknowledged' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                            po.status === 'in_progress' ? 'bg-cyan-50 text-cyan-700 border-cyan-100' :
                                            po.status === 'issued' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                            'bg-[#0088cc]/5 text-[hsl(var(--foreground))] border-[#0088cc]/20'
                                        )}>
                                            {po.status === 'in_progress' ? 'in fulfilment' : po.status}
                                        </div>
                                        {po.paymentStatus === 'paid' && (
                                            <div className="px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-emerald-500 text-white border border-emerald-600 shadow-sm">
                                                PAID
                                            </div>
                                        )}
                                        <span className="text-sm font-semibold text-[hsl(var(--muted-foreground))] hidden md:inline-block max-w-[200px] truncate">
                                            {po.title}
                                        </span>
                                    </div>

                                    <div className="flex flex-col xs:flex-row flex-wrap items-start sm:items-center gap-y-2 gap-x-4 sm:gap-x-6 min-w-0">
                                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-[hsl(var(--muted-foreground))] font-semibold min-w-0 max-w-full">
                                            <MdBusiness className="w-4 h-4 text-[#0088cc] flex-shrink-0" />
                                            <span className="truncate">{po.organizationId?.name || 'Customer'}</span>
                                            <MdArrowForward className="w-3 h-3 text-[#0088cc]/65 flex-shrink-0" />
                                            <span className="truncate">{po.vendorOrganizationId?.name || 'Vendor'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-[10px] sm:text-xs text-[#0088cc]/65 font-bold uppercase tracking-wider">
                                            <MdCalendarToday className="w-4 h-4 flex-shrink-0" />
                                            {new Date(po.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between gap-3 pt-3 lg:pt-0 border-t lg:border-t-0 border-[#0088cc]/10 w-full lg:w-auto lg:min-w-[180px]">
                                <div className="text-left lg:text-right min-w-0">
                                    <p className="text-[10px] font-black text-[#0088cc]/65 uppercase tracking-widest mb-1 group-hover:text-[#0088cc] transition-colors">Valuation</p>
                                    <p className="text-xl sm:text-2xl font-black text-[hsl(var(--foreground))] leading-none break-all">
                                        <span className="text-xs sm:text-sm font-bold text-[#0088cc]/65 mr-1">{po.currency}</span>
                                        {po.totalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </p>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {!['draft', 'issued'].includes(po.status) && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/dashboard/admin/shipments?search=${encodeURIComponent(po.poNumber)}`);
                                            }}
                                            className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-[#0088cc] text-white rounded-xl font-bold text-[11px] tracking-widest uppercase hover:bg-[#0077b3] transition-all shadow-md hover:shadow-blue-500/20 active:scale-95"
                                        >
                                            <MdLocalShipping className="w-4 h-4" />
                                            Track
                                        </button>
                                    )}
                                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0088cc]/5 border border-[#0088cc]/15 flex items-center justify-center text-[#0088cc]/65 flex-shrink-0 group-hover:text-[#0088cc] group-hover:border-[#0088cc] group-hover:bg-white transition-all shadow-xs">
                                        <MdArrowForward className="w-5 h-5" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="p-10 sm:p-20 text-center bg-white rounded-3xl border border-[#0088cc]/15 mt-4 shadow-sm border-dashed dark:bg-[hsl(var(--card))]">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#0088cc]/5 rounded-full flex items-center justify-center text-[#0088cc]/30 mx-auto mb-6 border border-[#0088cc]/15">
                            <MdReceipt className="w-8 h-8 sm:w-10 sm:h-10" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-black text-[hsl(var(--foreground))] mb-2">No matching orders found</h3>
                        <p className="text-sm sm:text-base text-[hsl(var(--muted-foreground))] font-medium max-w-sm mx-auto mb-8">
                            Try adjusting your filters or search query to find the purchase orders you're looking for.
                        </p>
                    </div>
                )}
            </div>
            {filteredOrders.length > 0 && (
                <Pagination
                    currentPage = {currentPage}
                    totalPages = {totalPages}
                    onPageChange = {setPage}
                    itemsPerPage = {PAGE_SIZE}
                    totalItems = {filteredOrders.length}
                />
            )}
        </div>
    );
}
